/**
 * SplashScreen — public marketing landing for unauthenticated visitors.
 *
 * Rendered by AuthGate (apps/web/src/AuthGate.tsx) when there is no
 * active session. Anyone hitting app.pentestsolutions.uk without a
 * cookie sees this; after the user clicks "Sign in", they're handed
 * off to /auth/login which kicks the WorkOS hosted-auth flow.
 *
 * Sign-out lands here too, so it doubles as the post-logout home.
 *
 * Design + copy were iterated v1 → v9 in chat (PR #158); the standalone
 * mock at latest/splash-mock.html is the authoritative visual reference.
 * Keep this component and the mock in lockstep — both render the same
 * structure and tokens.
 *
 * Layout note (2026-07-01): pricing was moved to sit beside the sign-in
 * CTA in a two-column "close" row (rather than dangling as a lone card
 * at the very bottom), and vertical spacing was tightened throughout so
 * the page reads closer to a single viewport. Same treatment already
 * used for "Main Features" / "How You Run It".
 *
 * Component is purely presentational; AuthGate computes the sign-in
 * URL (with returnTo) and passes it in. No data-source / fetch calls
 * happen here.
 */

import React from 'react';

interface SplashScreenProps {
  /** Href the Sign in CTAs link to — typically /auth/login?returnTo=... */
  signInHref: string;
  /** Where "Request a free trial" points. Defaults to the self-serve signup
   *  page; deployments without self-serve can point it at a contact route. */
  signUpHref?: string;
  /**
   * Optional break-glass handler. When set, an "Emergency access" link
   * appears under the CTA helper text. Used by AuthGate to toggle into
   * the break-glass sign-in form when WorkOS is unreachable. Reserved
   * for the firm-breakglass branch.
   */
  onBreakGlassClick?: () => void;
}

export function SplashScreen({
  signInHref,
  signUpHref = '/signup',
  onBreakGlassClick,
}: SplashScreenProps) {
  return (
    <main style={styles.page}>
      <div style={styles.splash}>
        {/* TOP NAVY BAND */}
        <div style={styles.headerBand}>
          <div style={styles.wordmark}>
            <span style={styles.wordmarkName}>Pentest Solutions</span>
            <span style={styles.wordmarkTagline}>Penetration Testing Reports</span>
          </div>
          <a href={signInHref} style={styles.headerSignin}>Sign in</a>
        </div>

        <div style={styles.accentRule} />

        {/* HERO — headline left, pricing + trial CTA right.
            Pricing used to sit at the very foot of the page, below the story
            and the feature list, which meant the two things a visitor actually
            wants (what it costs, how to start) were the last things they saw
            and only after scrolling. Both now sit beside the headline. */}
        <section style={styles.hero}>
          <div style={styles.heroGrid}>
            <div>
              <div style={styles.sectionLabel}>A Pentester's Reporting Engine</div>
              <h1 style={styles.heroHeadline}>Pentest Reporting, Rebuilt.</h1>
              <p style={styles.heroSubhead}>Clear reports for clients, time back for the testers.</p>

              <div style={styles.storyInline}>
                <div style={{ ...styles.sectionLabel, ...styles.storyLabel }}>The Story</div>
                <p style={styles.storyParagraph}>
                  Over the last decade as a senior penetration tester working across various UK cybersecurity companies, I've written hundreds, if not thousands, of reports, stitching engagements together across documents, fighting numbered headings, copy-pasting findings from last quarter's report, watching CVSS scores drift between phases. The reporting was eating the work: clients waited longer for documents that were harder to read, and the team was burning hours on formatting instead of testing. I needed a way out. Something that gives clients clear, concise reporting, and gives pentesters back their time to do what they do best.
                </p>
              </div>
            </div>

            <div style={styles.pricingColumn}>
              <div style={styles.sectionLabel}>Pricing &middot; 2026</div>
              <div style={styles.priceCard}>
                <div style={styles.priceTier}>Standard</div>
                <div style={styles.priceAmountRow}>
                  <span style={styles.priceAmount}>&pound;1,200</span>
                  <span style={styles.priceCadence}>/ user / year</span>
                </div>
                <div style={styles.priceAlt}>or &pound;120 / user / month</div>
                <div style={styles.priceMeta}>Save ~17% paying annually</div>
                <div style={styles.priceIncludes}>
                  Every feature and every assessment type is standard. One tier, no add-ons.
                </div>
              </div>
              <a href={signUpHref} style={styles.trialButton}>
                Request A Free 7-Day Trial &rarr;
              </a>
              <div style={styles.trialHelper}>
                No card required. Full access for 7 days, and bring up to four colleagues to
                explore it with you.
              </div>
              <div style={styles.restoreLine}>
                Workspace closed? <a href="/billing/restore" style={styles.restoreLink}>Restore access</a>
              </div>
            </div>
          </div>
        </section>

        {/* FEATURES STRIP — features + deployment + promise share surface-alt */}
        <section style={styles.features}>
          {/* Main features + deployment options, side by side to keep the page condensed */}
          <div style={styles.featuresTwoCol}>
            <div>
              <div style={styles.sectionLabel}>Some of the Main Features</div>
              <div style={styles.subtagline}>What you get on day one.</div>
              <div style={styles.featureList}>
                <FeatureCard
                  title="Built to Meet NCSC CHECK Reporting Standards"
                  desc="Templates shaped around the UK government's pentest reporting requirements, so your deliverables meet CHECK expectations out of the box."
                />
                <FeatureCard
                  title="All-in-One Pentest Reporting Platform"
                  desc="Straight-to-PDF reports, polished and client-ready, with a built-in client and sales database, engagement scheduling, and multi-phase engagements consolidated in minutes."
                />
                <FeatureCard
                  title="Managed Authentication"
                  desc="Sign-in is powered by WorkOS AuthKit."
                />
              </div>
            </div>

            <div>
              <div style={styles.sectionLabel}>How You Run It</div>
              <div style={styles.subtagline}>Self-hosted, and fully yours.</div>
              <div style={styles.featureList}>
                <FeatureCard
                  title="Self-Hosted, Always"
                  desc="Your servers, your data, your control. Everything runs in Docker, deployable on your own VPS or on-prem in minutes, with the deployment instructions we provide."
                />
                <FeatureCard
                  title="You Own the Infrastructure"
                  desc="You host it, you run it, and you keep full control of the product and every byte of client data. Nothing leaves your environment, and nothing is managed by us."
                />
              </div>
            </div>
          </div>

          {/* THE PROMISE */}
          <div style={styles.subsectionBlock}>
            <div style={{ ...styles.sectionLabel, marginBottom: 10 }}>The Promise</div>
            <div style={styles.promiseBody}>
              Everything is in the box. Today, and every feature we add tomorrow ships as standard.
            </div>
            <div style={styles.promiseSub}>
              Built for contractors, freelancers, and Penetration Testing Companies across the UK.
            </div>
          </div>
        </section>

        {/* CLOSE — a single line.
            This used to carry a SECOND "Sign in" button plus a duplicate of the
            pricing card. Two sign-ins on one page is a choice the visitor
            shouldn't have to make (they are the same link), and repeating the
            price just pushed the page past a scroll for no gain. The one
            sign-in now lives in the header band, in crimson, where a returning
            user looks first. */}
        <section style={styles.close}>
          <h2 style={styles.ctaHeadline}>More Time Testing, Less Time Formatting.</h2>
          {onBreakGlassClick && (
            <div style={styles.altSignInLine}>
              <button
                type="button"
                onClick={onBreakGlassClick}
                style={styles.breakGlassLink}
                data-debug-id="splash-break-glass-toggle"
              >
                Emergency access (firm admins only)
              </button>
            </div>
          )}
        </section>

        {/* FOOTER NAVY BAND */}
        <div style={styles.footerBand}>
          <div style={styles.footerLeft}>Pentest Solutions, Penetration Testing Reports</div>
          <div style={styles.footerRight}>UK-Built, for Pentest Firms</div>
        </div>
      </div>
    </main>
  );
}

interface FeatureCardProps {
  title: string;
  desc: string;
}
function FeatureCard({ title, desc }: FeatureCardProps) {
  return (
    <div style={styles.feature}>
      <div style={styles.featureTitleRow}>
        <span style={styles.featureTitle}>{title}</span>
      </div>
      <div style={styles.featureDesc}>{desc}</div>
    </div>
  );
}

// ── styles ──────────────────────────────────────────────────────────────────
// Inline styles intentional — splash uses Citadel-brand tokens (not the host's
// CSS variables) so the visual identity stays consistent regardless of the
// surrounding theme/mode. Same pattern as the standalone mock.

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh',
    background: '#f6f4f0',
    color: '#1a1f2e',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
    margin: 0,
    padding: 0,
  },
  splash: {
    maxWidth: 920,
    margin: '0 auto',
    background: '#f6f4f0',
    borderLeft: '0.5px solid #d0ccc4',
    borderRight: '0.5px solid #d0ccc4',
  },
  headerBand: {
    background: '#1a1f2e',
    color: '#f6f4f0',
    padding: '18px 56px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  wordmark: { display: 'flex', alignItems: 'baseline', gap: 12 },
  wordmarkName: { fontFamily: 'Georgia, serif', fontSize: 24, letterSpacing: '0.5px' },
  wordmarkTagline: {
    fontFamily: '"Courier New", monospace',
    fontSize: 10,
    letterSpacing: '3px',
    textTransform: 'uppercase',
    color: 'rgba(246, 244, 240, 0.55)',
  },
  // The page's ONLY sign-in. Crimson because it is the single action a
  // returning user wants, and it previously read as plain navy text against a
  // navy band — easy to miss, which is partly why a second one existed lower
  // down.
  headerSignin: {
    fontFamily: 'Georgia, serif',
    fontSize: 14,
    color: '#ffffff',
    background: '#8b1a2e',
    textDecoration: 'none',
    padding: '9px 22px',
    letterSpacing: '0.3px',
    display: 'inline-block',
  },
  accentRule: { height: 1.5, background: '#8b1a2e' },
  hero: { padding: '30px 56px 22px' },
  // Drops the pricing column down so it starts level with "The Story" rather
  // than with the headline. Sitting flush at the top left it stranded against
  // two lines of display type with nothing beside the story underneath; nudged
  // down, the card reads as sitting WITH the argument for buying rather than
  // floating above it, and the whitespace lands under the card instead of
  // between the two columns.
  pricingColumn: { marginTop: 132 },
  restoreLine: { marginTop: 16, fontSize: 12.5, color: '#7a7d8a', fontFamily: 'Arial, Helvetica, sans-serif', textAlign: 'center' as const },
  restoreLink: { color: '#8b1a2e', textDecoration: 'none', borderBottom: '0.5px solid #d0ccc4' },
  heroGrid: { display: 'grid', gridTemplateColumns: 'minmax(0, 1.15fr) minmax(300px, 0.85fr)', gap: 48, alignItems: 'start' },
  sectionLabel: {
    fontFamily: '"Courier New", monospace',
    fontSize: 10,
    letterSpacing: '3px',
    textTransform: 'uppercase',
    color: '#7a7d8a',
  },
  storyLabel: {
    borderTop: '0.5px solid #d0ccc4',
    paddingTop: 20,
    marginBottom: 14,
  },
  heroHeadline: {
    fontFamily: 'Georgia, serif',
    fontSize: 38,
    fontWeight: 'normal',
    lineHeight: 1.15,
    margin: '12px 0 10px',
    color: '#1a1f2e',
  },
  heroSubhead: {
    fontFamily: 'Georgia, serif',
    fontSize: 18,
    lineHeight: 1.5,
    color: '#555864',
    margin: 0,
    maxWidth: 560,
    fontStyle: 'italic',
  },
  story: { padding: '0 56px 12px', maxWidth: 760 },
  storyInline: { marginTop: 26, paddingTop: 18, borderTop: '0.5px solid #e0ddd8' },
  storyParagraph: {
    fontFamily: 'Georgia, serif',
    fontSize: 16,
    lineHeight: 1.7,
    color: '#1a1f2e',
    margin: '0 0 0.9em',
    // Justified with hyphenation OFF. The two settings are independent and the
    // damage came from hyphens, not from justification: with hyphens:auto the
    // browser broke words mid-air ("consultan-cies", "re-porting"), which reads
    // as a typo. Ragged-right fixed that but left visibly uneven line endings.
    // Justified + no hyphens gives clean edges with every word whole; the em
    // dashes were removed from the copy for the same reason, since a long
    // unbreakable run is what forces ugly spacing in a column this width.
    textAlign: 'justify',
    hyphens: 'none',
    WebkitHyphens: 'none',
    textJustify: 'inter-word',
  },
  storyClosing: { fontWeight: 500, margin: '1em 0 0' },
  features: {
    marginTop: 30,
    background: '#eae8e2',
    borderTop: '0.5px solid #d0ccc4',
    borderBottom: '0.5px solid #d0ccc4',
    padding: '28px 56px 32px',
  },
  subtagline: {
    fontFamily: 'Georgia, serif',
    fontStyle: 'italic',
    fontSize: 14,
    color: '#555864',
    margin: '6px 0 16px',
  },
  featureList: { display: 'flex', flexDirection: 'column', gap: 13 },
  featureGrid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 22 },
  featuresTwoCol: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 40, alignItems: 'start' },
  feature: {
    padding: '13px 18px',
    background: '#f6f4f0',
    borderLeft: '2px solid #1a1f2e',
  },
  featureTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 5,
    flexWrap: 'wrap',
  },
  featureTitle: { fontFamily: 'Georgia, serif', fontSize: 16, color: '#1a1f2e' },
  featureDesc: { fontSize: 13, lineHeight: 1.6, color: '#555864' },
  subsectionBlock: { marginTop: 22, paddingTop: 18, borderTop: '0.5px solid #d0ccc4' },
  promiseBody: {
    fontFamily: 'Georgia, serif',
    fontSize: 17,
    lineHeight: 1.5,
    color: '#1a1f2e',
    margin: '6px 0 6px',
    // No width cap. The old 640px forced a single sentence onto two lines with
    // one orphaned word ("standard.") on the second, which looks like an
    // accident rather than a statement. The section is ~900px wide, so letting
    // it use that puts each line on one line — and it still wraps naturally on
    // a narrow viewport rather than being pinned by nowrap.
    maxWidth: 'none',
  },
  promiseSub: {
    fontFamily: 'Georgia, serif',
    fontStyle: 'italic',
    fontSize: 15,
    lineHeight: 1.55,
    color: '#555864',
    maxWidth: 'none',
  },
  // CLOSE — sign-in CTA + pricing, two columns.
  close: {
    padding: '46px 56px 46px',
    borderTop: '0.5px solid #e0ddd8',
    textAlign: 'center' as const,
  },
  closeGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 48, alignItems: 'start' },
  ctaHeadline: {
    fontFamily: 'Georgia, serif',
    fontSize: 34,
    fontWeight: 'normal',
    margin: 0,
    color: '#1a1f2e',
    // Full width and centred. It was capped at 320px because it used to sit in
    // a two-column grid next to the pricing card, which wrapped a seven-word
    // statement onto three cramped lines. With that column gone it can be what
    // it was always meant to be: one line, across the page, read at a glance.
    lineHeight: 1.2,
    textAlign: 'center' as const,
    letterSpacing: '0.2px',
  },
  ctaButton: {
    display: 'inline-block',
    padding: '14px 32px',
    background: '#8b1a2e',
    color: '#f6f4f0',
    fontFamily: 'Georgia, serif',
    fontSize: 15,
    textDecoration: 'none',
    letterSpacing: '0.5px',
    borderRadius: 0,
  },
  ctaHelper: { marginTop: 18, fontSize: 12, color: '#7a7d8a', maxWidth: 340, lineHeight: 1.5 },
  altSignInLine: { marginTop: 12 },
  altSignInLink: {
    fontFamily: 'Georgia, serif',
    fontStyle: 'italic',
    fontSize: 12.5,
    color: '#8b1a2e',
    textDecoration: 'none',
    borderBottom: '0.5px solid #8b1a2e',
  },
  breakGlassLink: {
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    fontFamily: '"Courier New", monospace',
    fontSize: 11,
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
    color: '#8b1a2e',
    textDecoration: 'none',
  },
  footerBand: {
    background: '#1a1f2e',
    color: 'rgba(246, 244, 240, 0.55)',
    padding: '18px 56px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontSize: 11,
  },
  footerLeft: {
    fontFamily: '"Courier New", monospace',
    letterSpacing: '2.5px',
    textTransform: 'uppercase',
  },
  footerRight: { fontFamily: 'Georgia, serif', fontStyle: 'italic' },
  // Pricing card (right column of the close row).
  priceCard: {
    background: '#ffffff',
    border: '0.5px solid #d0ccc4',
    borderTop: '2px solid #8b1a2e',
    padding: '22px 26px 24px',
    maxWidth: 380,
  },
  priceTier: {
    fontFamily: '"Courier New", monospace',
    fontSize: 11,
    letterSpacing: '2.5px',
    textTransform: 'uppercase',
    color: '#7a7d8a',
    marginBottom: 12,
  },
  priceAmountRow: { display: 'flex', alignItems: 'baseline', gap: 8 },
  priceAmount: { fontFamily: 'Georgia, serif', fontSize: 38, color: '#1a1f2e', lineHeight: 1 },
  priceCadence: { fontFamily: '"Courier New", monospace', fontSize: 12, color: '#7a7d8a' },
  priceAlt: { fontFamily: 'Georgia, serif', fontSize: 15, color: '#555864', marginTop: 8 },
  priceMeta: {
    fontFamily: '"Courier New", monospace',
    fontSize: 11,
    letterSpacing: '0.5px',
    color: '#7a7d8a',
    marginTop: 10,
  },
  trialButton: {
    display: 'block',
    marginTop: 14,
    padding: '13px 22px',
    background: '#1a1f2e',
    color: '#ffffff',
    textDecoration: 'none',
    fontFamily: 'Georgia, serif',
    fontSize: 14,
    letterSpacing: '0.3px',
    textAlign: 'center' as const,
  },
  trialHelper: {
    marginTop: 9,
    fontSize: 11.5,
    color: '#7a7d8a',
    textAlign: 'center' as const,
    lineHeight: 1.5,
  },
  priceIncludes: {
    fontFamily: 'Georgia, serif',
    fontStyle: 'italic',
    fontSize: 14,
    lineHeight: 1.55,
    color: '#555864',
    marginTop: 14,
    maxWidth: 320,
  },
};

export default SplashScreen;
